import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useFormik } from 'formik';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useExerciseResultsForSetAutofill } from '../../../hooks/use-exercise-results-for-set-autofill';
import {
  isActivityTypeSubmitBlocked,
  resolveActivityTypeIdForSubmit,
  useTrainingActivityTypePicker,
  useTrainingActivityTypePreselect,
} from '../../../hooks/use-training-activity-type-picker';
import { useNotFound } from '../../../hooks/useNotFound';
import { useTrainingExercises } from '../../../hooks/useTrainingExercises';
import { useGetExerciseOptions } from '../../../redux/api/exerciseConfigAPI';
import {
  useGetTrainingById,
  usePostPutTraining,
} from '../../../redux/api/trainingsAPI';
import { useGetUserTrainingAccessIncoming } from '../../../redux/api/userTrainingAccessApi';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { TrainingStatus } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { ITraining } from '../../../types/interfaces/training';
import { IUser } from '../../../types/interfaces/user';
import { groupBy, orderBy } from '../../../utils/collections/array-utils';
import { getUserViewName } from '../../../utils/common/shared-util';
import { orderTraining } from '../../../utils/training/set-utils';
import { accessRowToIUser } from '../../users/settings/access-settings/access-settings-helpers';
import {
  getExerciseResultsUserIdForTrainingForm,
  getTrainingOwnerFormFields,
} from '../shared/training-owner-form';
import { getTrainingFormValidation } from './FormikData';
import { useEditTraining } from './useEditTraining';

export function useEditTrainingPage(trainingId?: string) {
  const { t } = useTranslation();
  const { navigate, back } = useNavigationStack();
  const { data: training, isFetching } = useGetTrainingById(trainingId);
  const { data: user } = useGetMyDetails();
  const { data: userTrainingAccess } = useGetUserTrainingAccessIncoming();
  const [postPutTraining, { isLoading }] = usePostPutTraining();
  const { exercises, isLoading: isTrainingExercisesLoading } =
    useTrainingExercises(trainingId);
  const [showSavedTrainingModal, setShowSavedTrainingModal] = useState(false);
  const [searchParams] = useSearchParams();
  const editParam = searchParams.get('edit');
  const { data: exerciseOptions } = useGetExerciseOptions();

  const orderedTraining: ITraining = useMemo(() => {
    if (
      !training ||
      !exercises ||
      !exerciseOptions ||
      isTrainingExercisesLoading
    ) {
      return null;
    }

    return orderTraining(training, exercises, exerciseOptions);
  }, [training, exercises, exerciseOptions, isTrainingExercisesLoading]);

  const createTrainingRecipients = userTrainingAccess?.createTraining ?? [];
  const canCreateForOthers = createTrainingRecipients.length > 0;

  const ownerSelectOptions = useMemo(
    () =>
      orderBy(
        createTrainingRecipients.map((row) => {
          const u = accessRowToIUser(row);
          return {
            ...u,
            displayName: getUserViewName(u),
          };
        }),
        ['displayName'],
        ['asc'],
      ),
    [createTrainingRecipients],
  );

  const canChangeOwner = training?.permissions?.canChangeOwner !== false;

  const ownerInitial = useMemo(
    () => getTrainingOwnerFormFields(orderedTraining, user),
    [orderedTraining, user],
  );

  useEffect(() => {
    if (training && !training?.permissions?.canEdit) {
      navigate(Paths.trainings.toViewId(trainingId), { replace: true });
    } else if (training?.status === TrainingStatus.started) {
      navigate(Paths.trainings.toCurrent(trainingId, editParam === 'true'), {
        replace: true,
      });
    } else if (training?.status === TrainingStatus.finished) {
      navigate(Paths.trainings.toFinished(trainingId, editParam === 'true'), {
        replace: true,
      });
    }
  }, [training]);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: getTrainingFormValidation(t),
    initialValues: {
      title: orderedTraining?.title || '',
      description: orderedTraining?.description || '',
      trainingSets: orderedTraining?.trainingSets || [],
      planedStartDate:
        orderedTraining?.status === TrainingStatus.notStarted
          ? orderedTraining?.planedStartDate || null
          : undefined,
      activityTypeId: orderedTraining?.activityTypeId ?? null,
      createForOthers: ownerInitial.createForOthers,
      trainingOwner: ownerInitial.trainingOwner as IUser | null,
    },
    onSubmit,
  });

  useTrainingActivityTypePreselect(formik);

  const {
    activityTypes,
    isLoading: isActivityTypesLoading,
    isError: isActivityTypesError,
    defaultActivityTypeId,
  } = useTrainingActivityTypePicker();

  const isSubmitDisabled = isActivityTypeSubmitBlocked(
    isActivityTypesLoading,
    activityTypes,
    formik.values.activityTypeId,
    defaultActivityTypeId,
    isActivityTypesError,
  );

  function onSubmit(
    values: ITraining & {
      createForOthers?: boolean;
      trainingOwner?: IUser | null;
    },
  ) {
    const { createForOthers, trainingOwner, ...rest } = values;
    const ownerId =
      createForOthers && trainingOwner?.id ? trainingOwner.id : user?.id;

    return postPutTraining({
      id: trainingId,
      body: {
        ...training,
        ...rest,
        ownerId,
        activityTypeId: resolveActivityTypeIdForSubmit(
          values.activityTypeId,
          defaultActivityTypeId,
          activityTypes,
        ),
        trainingSets: rest.trainingSets,
      },
    })
      .unwrap()
      .then(() => {
        back();
      })
      .catch((error) => console.log(error));
  }

  const exerciseResultsUserId = useMemo(
    () =>
      getExerciseResultsUserIdForTrainingForm(
        formik.values.trainingOwner,
        formik.values.createForOthers,
        user?.id,
      ),
    [formik.values.trainingOwner, formik.values.createForOthers, user?.id],
  );
  const exerciseResults = useExerciseResultsForSetAutofill(
    exerciseResultsUserId,
  );

  const onCreateForOthersChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      formik.setFieldValue('createForOthers', checked);
      if (checked && ownerSelectOptions[0]) {
        formik.setFieldValue('trainingOwner', ownerSelectOptions[0]);
      } else {
        formik.setFieldValue('trainingOwner', null);
      }
    },
    [formik, ownerSelectOptions],
  );

  const performerSelectOptions = useMemo(() => {
    let base =
      formik.values.createForOthers && canCreateForOthers
        ? ownerSelectOptions
        : [];
    const owner = formik.values.trainingOwner as
      | (IUser & { displayName?: string })
      | null;
    if (owner?.id && !base.some((o) => o.id === owner.id)) {
      base = [{ ...owner, displayName: getUserViewName(owner) }, ...base];
    }
    return base;
  }, [
    canCreateForOthers,
    formik.values.createForOthers,
    formik.values.trainingOwner,
    ownerSelectOptions,
  ]);

  const trainingSetGroups = useMemo(
    () =>
      formik.values?.trainingSets?.length
        ? groupBy(formik.values?.trainingSets, 'setNumber')
        : null,
    [formik.values.trainingSets],
  );

  function onCancelClick() {
    formik.resetForm();
    back();
  }

  const {
    onChangeTrainingSet,
    onRemoveTrainingSet,
    getSetIndex,
    onAddEmptyTrainingSet,
    onAddTrainingSetFromSavedProgramSet,
    onChangeSetOrder,
  } = useEditTraining(formik, trainingId);

  useNotFound(!isFetching && !training);

  return {
    formik,
    isLoading,
    isSubmitDisabled,
    training,
    user,
    canCreateForOthers,
    canChangeOwner,
    performerSelectOptions,
    onCreateForOthersChange,
    exerciseResults,
    onAddEmptyTrainingSet: () => onAddEmptyTrainingSet(trainingId),
    onChangeTrainingSet,
    onRemoveTrainingSet,
    trainingSetGroups,
    getSetIndex,
    onCancelClick,
    showSavedTrainingModal,
    setShowSavedTrainingModal,
    onAddTrainingSetFromSavedProgramSet,
    onChangeSetOrder,
  };
}
